<script setup lang="ts">
import { ref, onBeforeMount, computed, watch } from "vue";

// Pinia
import Store from "@store/dataProvider";
import { storeToRefs } from "pinia";

//composables
import { useToast } from "../../composables/useToast";
import { IAgents } from "./../../types/agents.type";

//types
import { FieldsTable, IPaginationResp } from "../../types/index";
import { IClient } from "./../../types/client.type";
import { ITemplateUI } from "../../types/templatesUI.type";
import { ICompany } from "../../types/company.type";

// repositories
import AgentsDataProviderRepository from "@repositories/agents.repository";
import ClientsDataProviderRepository from "@repositories/clients.repository";
import TemplatesUIDataProviderRepository from "./../../repositories/templatesUI.repository";
import CompaniesRepository from "@repositories/companies.repository";

// utils
import { getFieldsAgents } from "@services/forms/fieldsAgents";
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingAgents = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { agents }: any = storeToRefs(store);

const { $toast } = useToast();

const agentsProviderController = new AgentsDataProviderRepository();
const clientsProviderController = new ClientsDataProviderRepository();
const templatesUIRepository = new TemplatesUIDataProviderRepository();

const agentToDelete = ref<IAgents | null>(null);
const agentToEdit = ref<IAgents | null>(null);
const clientsData = ref<Array<IClient>>([]);
const companies = ref<ICompany[]>([]);
const templateData = ref<Array<ITemplateUI>>([]);
const isModalOpen = ref<boolean>(false);
const modalType = ref<string | null>(null);
const modalTitle = ref<string>();
const paginationData = ref<IPaginationResp>({
  current_page: 1,
});
const fieldsActives = ref<Array<FieldsTable>>([]);
const fieldsInactives = ref<Array<FieldsTable>>([]);
const valueMultiselect = ref<Array<any>>([]);
const deletedValues = ref<boolean>(false);
const searchFormRef = ref<any>(null);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.agentes.buscar_por_nombre'),
    value: null,
    cols: 3,
  },
]);

const fieldsUpdateUser = computed(() => {
  return [
    {
      key: "name",
      title: t("ajustes.agentes.nombre_del_agente"),
      type: "text",
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t("global.es_obligatorio"),
        },
      },
      placeholder: t("ajustes.agentes.nombre_del_agente"),
      value: agentToEdit.value?.name,
      showMarkRequired: true,
      cols: 12,
    },
    {
      key: "email",
      title: t("ajustes.agentes.email_del_agente"),
      type: "email",
      validations: {
        rules: {
          required: true,
          email: true,
        },
        messages: {
          required: t("global.es_obligatorio"),
          email: t("global.email_invalido"),
        },
      },
      placeholder: t("ajustes.agentes.email_del_agente"),
      value: agentToEdit.value?.email,
      showMarkRequired: true,
      cols: 12,
    },
    {
      key: "rfc",
      title: t("ajustes.agentes.rfc_del_agente"),
      type: "text",
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t("global.es_obligatorio"),
        },
      },
      placeholder: t("ajustes.agentes.rfc_del_agente"),
      value: agentToEdit.value?.rfc,
      showMarkRequired: true,
      cols: 12,
    },
    {
      key: "urlDomain",
      title: t("ajustes.agentes.urlDomain"),
      type: "text",
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t("global.es_obligatorio"),
        },
      },
      placeholder: t("ajustes.agentes.urlDomain"),
      value: agentToEdit.value?.urlDomain,
      showMarkRequired: true,
      cols: 12,
    },
    {
      key: "templateId",
      label: "name",
      value: agentToEdit.value?.templateId,
      searchable: true,
      title: t("ajustes.agentes.plantilla"),
      type: "select",
      placeholder: t("global.opcional"),
      formatValue: (val: any) => val?.id,
      cols: 12,
      options: templateData,
    },
    {
      key: "clients",
      // label: 'tradeName',
      customLabel: (e: any) => `${e.tradeName} - [${e?.cuit || e?.rut || e?.nif || e?.rfc}]`,
      searchable: true,
      title: t("ajustes.agentes.selecciona_los_cliente_asociados"),
      type: "select",
      placeholder: t("ajustes.agentes.debe_incluir_minimo_uno"),
      value: valueMultiselect.value,
      cols: 12,
      validations: {
        rules: {
          required: false,
        },
        messages: {
          required: t("global.es_obligatorio"),
        },
      },
      options: clientsData,
      multiple: true,
    },
    {
      key: "companies",
      label: "name",
      selectLabel: null,
      value: agentToEdit.value?.companies || [],
      searchable: true,
      title: t("global.compania"),
      trackBy: "id",
      validations: {
        rules: {
          required: true,
        },
      },
      type: "select",
      multiple: true,
      placeholder: t("ajustes.reaseguro.selecciona_companias"),
      formatValue: (val: any) => val.map((v: any) => v.id),
      showMarkRequired: true,
      cols: 12,
      options: companies,
    },
  ];
});

watch(agentToEdit, (newValue) => {
  if (newValue?.clients && Array.isArray(newValue?.clients)) {
    valueMultiselect.value = newValue?.clients.filter((elem: any) => elem.id);
  } else {
    valueMultiselect.value = [];
  }
});

const getCompaniesFromApi = async () => {
  try {
    const data = await CompaniesRepository.getCompanies(true);
    if (Array.isArray(data?.companies)) {
      companies.value = data.companies;
    }
  } catch (error) {
    console.error("Error while fetching companies:", error);
  }
};

const getAgentsApi = async (page: number, isDeleted?: boolean, search: string = '') => {
  isLoadingAgents.value = true;
  try {
    const response = await agentsProviderController.indexAgents({
        page,
        deleted: isDeleted,
        search
      });
    if (!response) return;
    if (Array.isArray(response?.data) && response?.pagination) {
      paginationData.value = response.pagination;
      store.setProp({ key: "agents", value: response.data });
    } else {
      store.setProp({ key: "agents", value: [] });
    }
  } finally {
    isLoadingAgents.value = false;
  }
};

const getAgentsApiPro = async (page: number, isDeleted?: boolean, params = {}) => {
  isLoadingAgents.value = true;
  try {
    const response = await agentsProviderController.indexAgents({
      page,
      deleted: isDeleted,
      ...params,
    });
    if (!response) return;
    if (Array.isArray(response?.data) && response?.pagination) {
      paginationData.value = response.pagination;
      store.setProp({ key: "agents", value: response.data });
    } else {
      store.setProp({ key: "agents", value: [] });
    }
  } catch (error) {
    console.error('Error while fetching agents:', error);
  } finally {
    isLoadingAgents.value = false;
  }
};

const getClientsAPI = async () => {
  const response = await clientsProviderController.indexClient();
  if (!response) return;
  if (Array.isArray(response?.data)) {
    clientsData.value = response.data;
  } else {
    clientsData.value = [];
  }
};

onBeforeMount(async () => {
  fieldsActives.value = getFieldsAgents();
  fieldsInactives.value = getFieldsAgents().slice();
  fieldsInactives.value.splice(4, 1);
  fieldsInactives.value.splice(4, 1, {
    key: "delete",
    label: t("global.restaurar"),
    thClass: "text-center",
  });
  await getAgentsApi(1);
  await getClientsAPI();
  await getCompaniesFromApi();
  const response = await templatesUIRepository.getTemplates();
  if (response?.data) templateData.value = response.data;
});

const handleModalDelete = (item: IAgents) => {
  modalTitle.value = !deletedValues.value
    ? `${t("ajustes.agentes.borrar_agente_con_el_id")}: ${item.id}`
    : `${t("ajustes.agentes.restaurar_agente_con_el_id")}: ${item.id}`;
  modalType.value = "modalDelete";
  agentToDelete.value = item;
  isModalOpen.value = true;
};

const handleModalClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  agentToEdit.value = null;
  agentToDelete.value = null;
};

const handleModalEdit = (item: IAgents) => {
  modalTitle.value = `${t("ajustes.agentes.editar_agente_con_el_id")}: ${
    item.id
  }`;
  modalType.value = "modalUpdate";
  agentToEdit.value = item;
  isModalOpen.value = true;
};

const handleModalCreate = () => {
  modalTitle.value = t("ajustes.agentes.crear_nuevo_agente");
  modalType.value = "modalCreate";
  isModalOpen.value = true;
};

const handleDelete = async () => {
  if (agentToDelete?.value?.id) {
    const response = await agentsProviderController.deleteAgent(
      agentToDelete.value.id,
      deletedValues.value
    );
    if (response?.message) {
      $toast?.error(response.message);
    } else {
      getAgentsApi(paginationData.value.current_page, deletedValues.value);
      $toast?.success(t("ajustes.agentes.agente_eliminado_con_exito"));
    }
    agentToDelete.value = null;
    handleModalClose();
  }
};

const handleActive = () => {
  if (!deletedValues.value) return;
  getAgentsApi(1);
  deletedValues.value = false;
};

const handleInactive = () => {
  if (!!deletedValues.value) return;
  getAgentsApi(1, true);
  deletedValues.value = true;
};

const handleUpdateAgent = async ({ items, isFormValid }: any) => {
  let payload = items;
  if (!isFormValid) return;
  if (!items?.templateId) payload = { ...payload, templateId: null };
  if (agentToEdit.value?.id) {
    const response = await agentsProviderController.updateAgent(
      payload,
      agentToEdit.value.id
    );
    if (!response?.errores) {
      getAgentsApi(paginationData.value?.current_page);
      $toast?.success(
        t("ajustes.agentes.el_agente_a_sido_editado_correctamente")
      );
    } else {
      if (response?.errors && response.errors.length > 0) {
        response.errors.forEach((elem) => {
          $toast?.error(elem.msg);
        });
      }
      if (response?.message) {
        $toast?.error(response.message);
      }
    }
  }
  handleModalClose();
};

const handleCreateAgent = async ({ items, isFormValid }: any) => {
  let payload = items;
  if (!isFormValid) return;
  if (!items?.templateId) payload = { ...payload, templateId: null };
  const response = await agentsProviderController.createAgent(payload);
  if (!response?.errores) {
    $toast?.success(t("ajustes.agentes.el_agente_a_sido_creado_correctamente"));
    getAgentsApi(paginationData.value?.current_page);
  } else {
    if (response?.errors && response?.errors.length > 0) {
      response.errors.forEach((elem: any) => {
        $toast?.error(elem.msg);
      });
    }
    if (response?.message) {
      $toast?.error(response.message);
    }
  }
  handleModalClose();
};

const onChangePage = async (page: number) => {
  getAgentsApi(page, deletedValues.value);
};

const handleOnSubmit = async ({ items }: any) => {
  if (items.search && items.search.trim() !== '') {
    getAgentsApi(1, deletedValues.value, items.search);
  }
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getAgentsApi(1, deletedValues.value);
};
</script>

<template>
  <Layout :title="t('ajustes.agentes.agentes')" :textColor="'color-secondary'">
    <template #layout>
       <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>
      <div class="d-flex gap-2 justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t("global.activos") }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t("global.inactivos") }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleModalCreate"
        >
          <template #content>{{
            t("ajustes.agentes.crear_nuevo_agente")
          }}</template>
        </CButton>
      </div>
     
      <DataTable
        :loading="isLoadingAgents"
        :fields="!deletedValues ? fieldsActives : fieldsInactives"
        :items="agents"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(edit)="operationsData">
          <div class="d-flex justify-content-center">
            <CButton
              background="bg-white"
              :customClass="`p-1`"
              @click="handleModalEdit(operationsData.data.item)"
              type="button"
            >
              <template v-slot:content>
                <img src="/icons/editar.svg" alt="editar" />
              </template>
            </CButton>
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div class="d-flex justify-content-center">
            <CButton
              :background="!deletedValues ? 'bg-white' : 'bg-blue'"
              :customClass="`p-1`"
              type="button"
              @click="handleModalDelete(operationsData.data.item)"
            >
              <template v-slot:content>
                <img
                  :src="
                    !deletedValues
                      ? '/icons/basura.svg'
                      : '/icons/restaurar.svg'
                  "
                  alt="eliminar"
                />
              </template>
            </CButton>
          </div>
        </template>
      </DataTable>
      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleModalClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalDelete'">
          <h5 class="text-center">
            {{
              !deletedValues
                ? `${t(
                    "ajustes.agentes.esta_seguro_que_desea_eliminar_el_agente"
                  )} ${agentToDelete?.name}?`
                : `${t(
                    "ajustes.agentes.esta_seguro_que_desea_restaurar_el_agente"
                  )} ${agentToDelete?.name}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDelete"
            >
              <template #content>
                <div>
                  <span>{{ t("global.confirmar") }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
        <template v-if="modalType === 'modalUpdate'">
          <Form
            ref="refTableForm"
            :inputs="fieldsUpdateUser"
            @onSubmit="handleUpdateAgent"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t("global.actualizar") }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>
        <template v-if="modalType === 'modalCreate'">
          <Form
            ref="refTableForm"
            :inputs="fieldsUpdateUser"
            @onSubmit="handleCreateAgent"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t("ajustes.agentes.crear_nuevo_agente") }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>
      </BModal>
    </template>
  </Layout>
</template>

<style scoped></style>
